import { createBrowserRouter, Navigate, Outlet, useRouteError } from 'react-router-dom';
import { useMe } from './lib/hooks';
import { AppShell } from './components/AppShell';
import { Loading } from './components/ui';
import { Login, Signup, Forgot, Reset } from './pages/Auth';
import { AcceptInvite, AcceptShare } from './pages/Invites';
import { TenantPicker } from './pages/TenantPicker';
import { Home } from './pages/Home';
import { Restaurants } from './pages/Restaurants';
import { RestaurantDetail } from './pages/RestaurantDetail';
import { VisitPage } from './pages/Visit';
import { ReportPage } from './pages/Report';
import { Reports } from './pages/Reports';
import { Team } from './pages/Team';
import { Agenda } from './pages/Agenda';
import { Portal, PortalReport } from './pages/Portal';

/** Decide a tela inicial: login, seleção de plataforma, plataforma única ou portal do cliente. */
function Root() {
  const me = useMe();
  if (me.isLoading) return <div className="page"><Loading rows={3} /></div>;
  if (me.error || !me.data) return <Navigate to="/entrar" replace />;
  const { tenants, hasShares } = me.data;
  if (tenants.length === 1 && !hasShares) return <Navigate to={`/t/${tenants[0].id}`} replace />;
  if (tenants.length === 0 && hasShares) return <Navigate to="/portal" replace />;
  return <Navigate to="/plataformas" replace />;
}

function RequireAuth() {
  const me = useMe();
  if (me.isLoading) return <div className="page"><Loading rows={3} /></div>;
  if (me.error || !me.data) return <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <Outlet />;
}

function RouteError() {
  const err = useRouteError() as Error;
  return (
    <div className="auth">
      <div className="box">
        <div className="card">
          <div className="h1" style={{ marginBottom: 8 }}>Algo deu errado</div>
          <div className="sub" style={{ marginBottom: 16 }}>{err?.message ?? 'Erro inesperado.'}</div>
          <a className="btn dark" href="/">Voltar ao início</a>
        </div>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Root /> },
      { path: '/entrar', element: <Login /> },
      { path: '/cadastro', element: <Signup /> },
      { path: '/esqueci', element: <Forgot /> },
      { path: '/redefinir/:token', element: <Reset /> },
      { path: '/convite/:token', element: <AcceptInvite /> },
      { path: '/compartilhado/:token', element: <AcceptShare /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/plataformas', element: <TenantPicker /> },
          { path: '/portal', element: <Portal /> },
          { path: '/portal/relatorio/:vid', element: <PortalReport /> },
          { path: '/t/:tid/visita/:vid', element: <VisitPage /> },
          { path: '/t/:tid/relatorio/:vid', element: <ReportPage /> },
          {
            path: '/t/:tid',
            element: <AppShell />,
            children: [
              { index: true, element: <Home /> },
              { path: 'restaurantes', element: <Restaurants /> },
              { path: 'restaurantes/:rid', element: <RestaurantDetail /> },
              { path: 'restaurantes/:rid/:tab', element: <RestaurantDetail /> },
              { path: 'relatorios', element: <Reports /> },
              { path: 'agenda', element: <Agenda /> },
              { path: 'equipe', element: <Team /> },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
